import { Component, NgModule, Type } from '@angular/core';
import { RouterModule } from '@angular/router';

import { GATEKEEPER_TOGGLES } from '../common/constants/gatekeeper-toggles';
import { ZciRoutes } from './app-routing.config';
import { DynamicRouteComponent } from './components/dynamic-route/dynamic-route.component';
import { AboutPageComponent } from './components/pages/about-page/about-page.component';
import { AccountPermissionsPageComponent } from './components/pages/account-permissions-page/account-permissions-page.component';
import { IneligiblePageComponent } from './components/pages/ineligible-page/ineligible-page.component';
import { EditUsersPageComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/edit-users-page/edit-users-page.component';
import { RemoveFromAccountPageComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/remove-from-account-page/remove-from-account-page.component';
import { RemoveUsersPageComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/remove-users-page/remove-users-page.component';
import { AddExistingUserPageComponent } from './components/pages/interstitial-pages/users-interstitial-pages/add-existing-user-page/add-existing-user-page.component';
import { EditUserAccessPageComponent } from './components/pages/interstitial-pages/users-interstitial-pages/edit-user-access-page/edit-user-access-page.component';
import { RemoveUserAccessPageComponent } from './components/pages/interstitial-pages/users-interstitial-pages/remove-user-access-page/remove-user-access-page.component';
import { RemoveUserFromAccountPageComponent } from './components/pages/remove-user-from-account-page/remove-user-from-account-page.component';
import { TechnicalDifficultiesPageComponent } from './components/pages/technical-difficulties-page/technical-difficulties-page.component';
import { AccessDeniedComponent } from './components/pages/access-denied/access-denied.component';
import { UsersOnAccountPageComponent } from './components/pages/users-on-account-page/users-on-account-page.component';
import { ObservableAccountsComponent } from './components/tabs/observable-accounts/observable-accounts.component';
import { OwnAccountsComponent } from './components/tabs/own-accounts/own-accounts.component';
import { UsersComponent } from './components/tabs/users/users.component';
import { APPLICATION_ROUTES, PUBLIC_ROUTES } from './constants/application-routes';
import { InternalNavigationGuard } from './guards/internal-navigation.guard';
import { TechnicalDifficultiesRedirectGuard } from './guards/technical-difficulties-redirect.guard';
import { UsersOnAccountGuard } from './guards/users-on-account.guard';
import { MVP2GatekeeperGuard } from './guards/mvp2-gatekeeper.guard';
import { ContentResolver } from './resolvers/content.resolver';
import { AddUserToAccountPageMvpComponent } from './components/pages/add-user-to-account-page-mvp/add-user-to-account-page-mvp.component';
import { IneligibleScenariosResolver } from './resolvers/ineligible-scenarios.resolver';
import { GranteeLandingPageComponent } from './components/pages/grantee-landing-page/grantee-landing-page.component';
import { TokenValidationResolver } from './resolvers/token-validation.resolver';
import { GenericErrorComponent } from './components/pages/generic-error/generic-error.component';
import { GranteeWebRegPageComponent } from './components/pages/grantee-web-reg-page/grantee-web-reg-page.component';
import { GranteeWelcomePageComponent } from './components/stepper-steps/grantee-flow/grantee-welcome-page/grantee-welcome-page.component';
import { AlmostThereComponent } from './components/stepper-steps/grantee-flow/almost-there/almost-there.component';
import { TokenValidationPublicResolver } from './resolvers/token-validation-public.resolver';
import { AlmostThereResolver } from './resolvers/almost-there.resolver';
import { ClientSegments } from './interfaces/client-segment.enum';
import { RemoveFromAccountPagePIComponent } from './components/pages/interstitial-pages/accounts-interstitial-pages/remove-from-account-page-pi/remove-from-account-page-pi.component';
import { EditUserAccessPagePIComponent } from './components/pages/interstitial-pages/users-interstitial-pages/edit-user-access-page-pi/edit-user-access-page-pi.component';
import { ClientSegmentResolver } from './resolvers/client-segment.resolver';
import { InternalOnlyGuard } from './guards/internal-only.guard';

const routes: ZciRoutes = [
    {
        path: '',
        resolve: {
            clientSegment: ClientSegmentResolver,
            content: ContentResolver,
        },
        children: [
            {
                path: PUBLIC_ROUTES.GRANTEE_WEB_REG,
                component: GranteeWebRegPageComponent,
            },
            {
                path: PUBLIC_ROUTES.LITE_REGISTRATION_GRANTEE_WELCOME,
                canActivate: [MVP2GatekeeperGuard],
                component: GranteeWelcomePageComponent,
                resolve: {
                    validateToken: TokenValidationPublicResolver,
                },
            },
            {
                path: PUBLIC_ROUTES.LITE_REGISTRATION_ALMOST_THERE,
                component: AlmostThereComponent,
                resolve: {
                    data: AlmostThereResolver,
                },
            },
            {
                path: APPLICATION_ROUTES.HOME,
                component: AccountPermissionsPageComponent,
                children: [
                    {
                        path: APPLICATION_ROUTES.USERS,
                        component: UsersComponent,
                    },
                    {
                        path: APPLICATION_ROUTES.OWN_ACCOUNTS,
                        component: OwnAccountsComponent,
                    },
                    {
                        path: APPLICATION_ROUTES.OBSERVABLE_ACCOUNTS,
                        component: ObservableAccountsComponent,
                    },
                ],
            },
            { path: APPLICATION_ROUTES.ABOUT, component: AboutPageComponent },
            {
                path: APPLICATION_ROUTES.USERS_ON_ACCOUNT.FULL_PATH,
                component: UsersOnAccountPageComponent,
                canActivate: [UsersOnAccountGuard],
            },
            {
                path: APPLICATION_ROUTES.EDIT_USERS.FULL_PATH,
                component: DynamicRouteComponent,
                data: {
                    routePath: APPLICATION_ROUTES.OWN_ACCOUNTS,
                    dynamicClientSegmentComponent: {
                        featureId: ClientSegments.WM,
                        offComponent: <Type<Component>>EditUserAccessPagePIComponent,
                        onComponent: <Type<Component>>EditUsersPageComponent,
                    },
                },
            },
            {
                path: APPLICATION_ROUTES.REMOVE_USERS.FULL_PATH,
                component: DynamicRouteComponent,
                canActivate: [UsersOnAccountGuard],
                data: {
                    dynamicRouteComponent: {
                        featureId: GATEKEEPER_TOGGLES.REMOVE_USER_ACCESS_FROM_ACCOUNT_ENABLED,
                        offComponent: <Type<Component>>RemoveUsersPageComponent,
                        onComponent: <Type<Component>>RemoveUserFromAccountPageComponent,
                    },
                },
            },
            {
                path: APPLICATION_ROUTES.ADD_EXISTING_USER.FULL_PATH,
                component: AddExistingUserPageComponent,
            },
            {
                path: APPLICATION_ROUTES.EDIT_USER_ACCESS.FULL_PATH,
                component: DynamicRouteComponent,
                data: {
                    routePath: APPLICATION_ROUTES.USERS,
                    dynamicClientSegmentComponent: {
                        featureId: ClientSegments.WM,
                        offComponent: <Type<Component>>EditUserAccessPagePIComponent,
                        onComponent: <Type<Component>>EditUserAccessPageComponent,
                    },
                },
            },
            {
                path: APPLICATION_ROUTES.REMOVE_USER_ACCESS.FULL_PATH,
                component: DynamicRouteComponent,
                data: {
                    routePath: APPLICATION_ROUTES.USERS,
                    dynamicClientSegmentComponent: {
                        featureId: ClientSegments.WM,
                        offComponent: <Type<Component>>RemoveFromAccountPagePIComponent,
                        onComponent: <Type<Component>>RemoveUserAccessPageComponent,
                    },
                },
            },
            {
                path: APPLICATION_ROUTES.REMOVE_ACCESS_FROM_ACCOUNT.FULL_PATH,
                component: DynamicRouteComponent,
                data: {
                    routePath: APPLICATION_ROUTES.OBSERVABLE_ACCOUNTS,
                    dynamicClientSegmentComponent: {
                        featureId: ClientSegments.WM,
                        offComponent: <Type<Component>>RemoveFromAccountPagePIComponent,
                        onComponent: <Type<Component>>RemoveFromAccountPageComponent,
                    },
                },
            },
            {
                path: APPLICATION_ROUTES.ADD_USER_TO_ACCOUNT.FULL_PATH,
                component: AddUserToAccountPageMvpComponent,
            },
            {
                path: APPLICATION_ROUTES.INELIGIBLE.FULL_PATH,
                component: IneligiblePageComponent,
            },
            {
                path: APPLICATION_ROUTES.TECH_DIFF_REDIRECT,
                canActivate: [TechnicalDifficultiesRedirectGuard],
                component: TechnicalDifficultiesPageComponent,
            },
            {
                path: APPLICATION_ROUTES.TECHNICAL_DIFFICULTIES,
                component: TechnicalDifficultiesPageComponent,
                canDeactivate: [InternalNavigationGuard],
                canActivate: [InternalNavigationGuard],
            },
            {
                path: APPLICATION_ROUTES.ACCEPT_ACCESS,
                component: GranteeLandingPageComponent,
                resolve: {
                    validateToken: TokenValidationResolver,
                    ineligibleScenarios: IneligibleScenariosResolver,
                },
            },
            {
                path: APPLICATION_ROUTES.GENERIC_ERROR,
                component: GenericErrorComponent,
                canDeactivate: [InternalNavigationGuard],
                canActivate: [InternalNavigationGuard],
            },
            // ─── Multi-account routes ─────────────────────────────────────────────────
            // TODO: replace with dedicated multi-account page components
            {
                path: APPLICATION_ROUTES.ADD_USERS_MULTIACCOUNT,
                component: AddUserToAccountPageMvpComponent,
            },
            {
                path: APPLICATION_ROUTES.REMOVE_USERS_MULTIACCOUNT,
                component: RemoveUserFromAccountPageComponent,
            },
        ],
    },
    {
        path: PUBLIC_ROUTES.ACCESS_DENIED,
        component: AccessDeniedComponent,
        canActivate: [InternalOnlyGuard],
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppMultiAccountRoutingModule {}
